import { Box, LinearProgress, Typography, Tooltip } from '@mui/material'
import { useTheme } from '@mui/material/styles'

interface POProgressBarProps {
  valueUsed: number
  valueTotal: number
  showLabel?: boolean
}

export function POProgressBar({ valueUsed, valueTotal, showLabel = true }: POProgressBarProps) {
  const theme = useTheme()

  const percentage = valueTotal > 0 ? Math.min((valueUsed / valueTotal) * 100, 150) : 0
  const isOverrun = valueUsed > valueTotal

  // Determine colors
  const getBarColor = () => {
    if (isOverrun) return theme.palette.error.main
    if (percentage > 80) return theme.palette.warning.main
    return theme.palette.success.main
  }

  const formatEUR = (val: number) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(val)

  const barColor = getBarColor()

  return (
    <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1 }}>
      {showLabel && (
        <Typography variant="caption" sx={{ minWidth: 100, textAlign: 'right' }}>
          {percentage.toFixed(1)}%
        </Typography>
      )}
      <Tooltip
        title={
          <Box sx={{ p: 1 }}>
            <div>Used: {formatEUR(valueUsed)}</div>
            <div>Total: {formatEUR(valueTotal)}</div>
            <div>Remaining: {formatEUR(Math.max(0, valueTotal - valueUsed))}</div>
            {isOverrun && <div style={{ color: theme.palette.error.main }}>Overrun: {formatEUR(valueUsed - valueTotal)}</div>}
          </Box>
        }
      >
        <Box sx={{ flexGrow: 1, position: 'relative' }}>
          {/* Background bar (gray striped) */}
          <LinearProgress
            variant="determinate"
            value={100}
            sx={{
              height: 24,
              borderRadius: 1,
              backgroundColor: theme.palette.po?.stripedGray || '#E0E0E0',
              '& .MuiLinearProgress-bar': {
                background: `repeating-linear-gradient(
                  45deg,
                  ${theme.palette.action.disabledBackground},
                  ${theme.palette.action.disabledBackground} 10px,
                  ${theme.palette.action.disabled} 10px,
                  ${theme.palette.action.disabled} 20px
                )`,
              },
            }}
          />
          {/* Overlay bar (colored based on usage) */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              width: `${Math.min(percentage, 100)}%`,
              backgroundColor: barColor,
              borderRadius: 1,
              opacity: 0.8,
              transition: 'width 0.3s ease',
            }}
          />
          {/* Overrun indicator (from right) */}
          {isOverrun && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                height: '100%',
                width: `${Math.min(percentage - 100, 50)}%`,
                backgroundColor: theme.palette.po?.pastelRed || '#FFCDD2',
                borderRadius: 1,
                opacity: 0.9,
              }}
            />
          )}
        </Box>
      </Tooltip>
    </Box>
  )
}
